/**
 * Best-effort detection for whether the current site is muted via the
 * Chrome per-site "Sound" toggle (the one in the lock-icon site-settings
 * panel).
 *
 * There is no direct JS API for the site Sound permission. But we can use
 * AudioContext as a proxy: when a site is muted via the Chrome Sound
 * toggle, creating an AudioContext returns a context whose state is
 * "suspended" and that cannot be resumed without a user gesture (for
 * muted sites specifically, even a gesture can't resume it).
 *
 * This is a PROXY, not a perfect signal:
 * - Before any user gesture, a fresh AudioContext is ALWAYS suspended
 *   (autoplay policy). So if we call this on page load it will lie.
 * - We only call it AFTER a user click (the Enable button), so the
 *   autoplay-policy suspension is out of the way.
 * - If we're still suspended after a click + attempted resume, the most
 *   likely remaining cause is the per-site Sound mute.
 *
 * Returns 'audible' | 'muted' | 'unknown'.
 */

export async function detectSiteAudioMuted() {
  try {
    const Ctx = (typeof window !== 'undefined')
      && (window.AudioContext || window.webkitAudioContext);
    if (!Ctx) return 'unknown';

    const ctx = new Ctx();

    // If not suspended, site sound is working
    if (ctx.state === 'running') {
      try { await ctx.close(); } catch (_) { /* ignore */ }
      return 'audible';
    }

    // Suspended — try to resume. If the resume succeeds and state becomes
    // 'running', site sound is working (suspension was just autoplay policy).
    try {
      await ctx.resume();
    } catch (_) { /* ignore; we check state next */ }

    const afterResume = ctx.state;
    try { await ctx.close(); } catch (_) { /* ignore */ }

    if (afterResume === 'running') return 'audible';
    return 'muted';
  } catch (_) {
    return 'unknown';
  }
}
